import { forwardRef, type MouseEventHandler } from "react";
import { cn } from "@/lib/utils";
import { ArrowRight } from "./icons";

type Variant = "dark" | "light" | "ghost" | "graphite" | "outline" | "mint";
type Size = "sm" | "md" | "lg";

export type ButtonProps = {
  children: string;
  href?: string;
  variant?: Variant;
  size?: Size;
  /** "inline" = small arrow after the label, "circle" = arrow inside a filled disc. */
  arrow?: false | "inline" | "circle";
  /** Start collapsed so a parent timeline can grow it out of a dot (see `dotToPill`). */
  dot?: boolean;
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  type?: "button" | "submit";
  "aria-label"?: string;
  /** Pre-selects an option in a form on the page (e.g. the demo form's "I want to" choice). */
  "data-intent"?: string;
};

const sizes: Record<Size, string> = {
  sm: "h-8 px-3.5 text-[14px]",
  md: "h-10 px-5 text-[14px]",
  lg: "h-12 px-6 text-[14.5px]",
};

export const Button = forwardRef<HTMLAnchorElement & HTMLButtonElement, ButtonProps>(
  function Button(
    { children, href, variant = "mint", size = "md", arrow = false, dot, className, onClick, type = "button", ...aria },
    ref,
  ) {
    const classes = cn("btn", `btn-${variant}`, sizes[size], arrow === "circle" && "pr-1.5", className);
    const content = (
      <>
        <span className="btn-label" data-btn-label="">
          <span data-text={children}>{children}</span>
        </span>
        {arrow === "inline" && <ArrowRight className="btn-arrow size-3.5" />}
        {arrow === "circle" && (
          <span
            className={cn(
              "btn-arrow grid size-[22px] place-items-center rounded-full",
              variant === "dark" || variant === "ghost" || variant === "graphite"
                ? "bg-white text-ink"
                : "bg-ink text-white",
            )}
          >
            <ArrowRight className="size-3" />
          </span>
        )}
      </>
    );

    if (href) {
      return (
        <a ref={ref} href={href} className={classes} data-dot={dot ? "" : undefined} onClick={onClick} {...aria}>
          {content}
        </a>
      );
    }
    return (
      <button ref={ref} type={type} className={classes} data-dot={dot ? "" : undefined} onClick={onClick} {...aria}>
        {content}
      </button>
    );
  },
);
